import { AttributeReference } from '@gonfalon/contexts/attributes/AttributeReference';
import { getContextKey } from '@gonfalon/contexts/kind-key/getContextKey';
import { getContextKind } from '@gonfalon/contexts/kind-key/getContextKind';
import {
  makeKindKeyStringFromKindKey,
  makeKindKeyStringFromProps,
} from '@gonfalon/contexts/kind-key/makeKindKeyString';
import { isMobileContextKind } from '@gonfalon/contexts/kinds/isMobileContextKind';
import type { ContextKind, LDContextCommon } from '@gonfalon/contexts/types';
import { ContextKindKey } from '@gonfalon/contexts/types';
import {
  disableBuiltInContextNameAttributes,
  isMobileAppTargetingEnabled,
} from '@gonfalon/dogfood-flags/dogfood-flags';
import type { OrderedSet } from '@gonfalon/immutable-shim/immutableShim';
import { List, Map } from '@gonfalon/immutable-shim/immutableShim';
import type { GroupedOption, OptionProps } from '@gonfalon/launchpad-experimental/CustomSelect';
import { toContext, toHref } from '@gonfalon/navigator/navigators';
import type { schemas } from '@gonfalon/openapi/types';
import nullthrows from 'nullthrows';

import {
  APP_VERSION_SUPPORT_STATUS_ATTRIBUTE,
  CONTEXT_KIND_ATTRIBUTE,
  HIDDEN_MOBILE_ATTRIBUTES,
} from '#utils/constants';
import type { Target } from '#utils/flags/records/Target';
import type { SegmentTarget } from '#utils/segment/records/SegmentTarget';
import type { UserAttributesProps } from '#utils/userAttributeUtils';
import {
  builtinAttributes as userBuiltinAttributes,
  mobileAttributesMapping,
  standardAttributesMapping,
} from '#utils/userAttributeUtils';
import { createUser } from '#utils/userUtils';

import type {
  ContextAttributesName,
  ContextAttributesNames,
  ContextAttributesValues,
  ContextItem,
  ContextItemsEntities,
  ContextResponse,
  ContextTargetKindKey,
  Links,
  ResolveContextsGroups,
  ResolveContextsResponse,
} from '../types';
import type { LDContext } from '../types/LDContext';

const contextsBuilIntAttributes = ['key'];
const enableMobileTargeting = isMobileAppTargetingEnabled();

export const makeSelectOptionsForContextAttributeValues = (
  contextAttributesValuesList: ContextAttributesValues[],
  contextKind: string,
) => {
  const options: Array<{ label: string; value: string }> = [];
  const attributeValuesForContextKind = contextAttributesValuesList.find((c) => c.kind === contextKind);
  attributeValuesForContextKind?.values.forEach((v) => {
    const option = {
      label: v.name.toString(),
      value: v.name,
    };
    options.push(option);
  });

  return options;
};

const mergeBuiltInAttributes = (contextKind: string, attributes: ContextAttributesName[]) => {
  if (disableBuiltInContextNameAttributes()) {
    return attributes;
  }
  const mergedAttributes = [...attributes].map((a) => ({ ...a, name: a.name ?? '' }));
  const builtinAttributes = contextKind === ContextKindKey.USER ? userBuiltinAttributes : contextsBuilIntAttributes;
  builtinAttributes
    .filter((attr) => attr !== 'avatar' && attr !== 'secondary')
    .forEach((builtinAttribute) => {
      if (!attributes.some((attribute) => attribute.name === builtinAttribute)) {
        mergedAttributes.push({ name: builtinAttribute, count: 1 });
      }
    });
  return mergedAttributes.sort((a, b) => a.name.localeCompare(b.name));
};

export const makeSelectOptionsForContextKinds = (contextKinds: ContextKind[]) => {
  const options: Array<{ label: string; value: string }> = [];

  contextKinds.forEach((c) => {
    options.push({
      label: c.name !== '' ? c.name : c.key,
      value: c.key,
    });
  });

  return options;
};

export const makeSelectAttributeOptionsForContextKind = (
  contextKindKey: string | null | undefined,
  formValue: string | null | undefined,
  contextAttributesNamesList: ContextAttributesNames[],
) => {
  if (!contextKindKey) {
    return [];
  }

  const existingAttributes = contextAttributesNamesList.find((value) => value.kind === contextKindKey);
  let attributeNames: ContextAttributesName[] = [];

  if (existingAttributes) {
    attributeNames = existingAttributes.names;
  }

  const formatContextAttribute = (attribute: string) => new AttributeReference(attribute).toString();
  const attributes = mergeBuiltInAttributes(contextKindKey, attributeNames);
  const isMobileAtttribute = enableMobileTargeting && isMobileContextKind(contextKindKey);
  const attributeOptions = attributes.map((attribute) => {
    const option: {
      label: string;
      value: string;
      contextKind: string;
      redacted?: boolean;
    } = {
      label: standardAttributesMapping[attribute.name] || formatContextAttribute(attribute.name),
      value: attribute.name,
      contextKind: contextKindKey,
    };

    if (attribute.redacted) {
      option.redacted = true;
    }
    return option;
  });

  // include the form value in the options if it's not already in the options
  if (formValue && !attributeOptions.some((option) => option.value === formValue)) {
    attributeOptions.push({
      label: formValue,
      value: formValue,
      contextKind: contextKindKey,
    });
  }

  // Always include 'key' in the options. Ensure it is not already in the options.
  if (!attributeOptions.some((option) => option.value === 'key')) {
    attributeOptions.push({
      label: 'Key',
      value: 'key',
      contextKind: contextKindKey,
    });
  }
  let options = attributeOptions;

  if (isMobileAtttribute) {
    options = options
      .filter((option) => !HIDDEN_MOBILE_ATTRIBUTES.includes(option.value))
      .map((option) => {
        // additional mapping for mobile attributes
        if (isMobileAtttribute) {
          return {
            ...option,
            label: mobileAttributesMapping[option.value] || formatContextAttribute(option.value),
          };
        }
        return option;
      });
  }

  const addVersionSupportStatusOption =
    // if the option doesn't already exist and the flag is enabled
    !attributeOptions.some((option) => option.value.toLowerCase() === APP_VERSION_SUPPORT_STATUS_ATTRIBUTE) &&
    contextKindKey === ContextKindKey.APPLICATION;

  if (addVersionSupportStatusOption) {
    options.push({
      label: 'Version support status',
      value: APP_VERSION_SUPPORT_STATUS_ATTRIBUTE,
      contextKind: ContextKindKey.APPLICATION,
    });
  }

  return options;
};

export const augmentResolveContextsResponse = (response: ContextResponse): ResolveContextsResponse => {
  const entities = response.items.reduce((accum, item) => {
    const kindKeyString = makeKindKeyStringFromProps(item.context.kind, getContextKey(item.context));
    return { ...accum, [kindKeyString]: item };
  }, {});
  return { ...response, entities };
};

/**
 * Adapts the generated `searchContexts` response — whose `ContextRecord.context` is typed `unknown` —
 * into our domain {@link ContextResponse}. Coercing the generated shape to the domain type in this one
 * place lets query consumers (`select`) work with typed {@link ContextItem}s instead of casting at
 * every call site.
 */
export const toContextResponse = (response: schemas['Contexts']): ContextResponse => ({
  _environmentId: response._environmentId,
  continuationToken: response.continuationToken,
  totalCount: response.totalCount,
  // The generated `ContextRecord` types `context` as `unknown` and carries extra fields; reinterpret
  // each record as our domain `ContextItem` here — the single boundary between generated and domain.
  items: response.items.map((item) => item as unknown as ContextItem),
});

/**
 * Wraps a bare context in a minimal {@link ContextItem}. Only `.context` and `._links.site`
 * are read downstream, and `_links.site` falls back to a link derived from kind/key in
 * {@link createUserFromContextItem}, so the empty `_links` is safe.
 */
export const contextItemFromContext = (context: LDContextCommon): ContextItem =>
  ({ context, _links: {} }) as unknown as ContextItem;

export const getContextsWithMissingNames = (contextsResponse: ContextResponse) => {
  const c = contextsResponse.items;
  const missingContextNames = c
    .filter((item) => !item.context?.name)
    .map((item) => `${item.context.kind}:${item.context.key}`);
  return missingContextNames;
};

export const monkeyPatchContextItemsWithNames = (
  contextsResponse: ContextResponse,
  contextsResponseWithNames: ContextResponse,
) => {
  const updatedItems = contextsResponse.items.map((contextItem) => {
    const contextItemWithNames = contextsResponseWithNames.items.find(
      (item) => item.context.kind === contextItem.context.kind && item.context.key === contextItem.context.key,
    );
    if (!contextItemWithNames) {
      return contextItem;
    } else {
      return {
        ...contextItem,
        context: {
          ...contextItem.context,
          name: contextItemWithNames.context?.name,
        },
      } as ContextItem;
    }
  });
  const responseWithNames = {
    ...contextsResponse,
    items: updatedItems,
  };
  return responseWithNames;
};

export const getContextItemSiteLink = (contextItem: ContextItem, projKey: string, envKey: string) =>
  toContext({
    projectKey: projKey,
    environmentKey: envKey,
    kind: contextItem.context.kind,
    key: String(nullthrows(contextItem.context.key)),
  });

export const createUserFromContextItem = (contextItem: ContextItem, projKey: string, envKey: string) => {
  const siteLink = { href: toHref(getContextItemSiteLink(contextItem, projKey, envKey)), type: 'text/html' };
  const links: Links = { ...contextItem._links, site: contextItem._links.site || siteLink };

  const contextAttributes = contextItem.context;
  const attributes = userBuiltinAttributes.reduce((accum, builtIn) => {
    const builtInValue = contextAttributes[builtIn];
    if (builtInValue) {
      return { ...accum, [builtIn]: builtInValue };
    } else {
      return accum;
    }
  }, {} as UserAttributesProps);

  const customAttributes = Map(
    Object.keys(contextAttributes).reduce(
      (accum, attribute) => {
        if (!userBuiltinAttributes.includes(attribute)) {
          return { ...accum, [attribute]: contextAttributes[attribute] };
        } else {
          return accum;
        }
      },
      {} as Map<string, string | List<string>>,
    ),
  );

  attributes.custom = customAttributes;

  return createUser({ _links: links, attributes });
};

const getMatchedKindKeyFromEntities = (
  entities: ContextItemsEntities,
  requestedKindKeyOrEmail: ContextTargetKindKey,
): ContextTargetKindKey => {
  const userMatchByEmail =
    requestedKindKeyOrEmail.kind === ContextKindKey.USER
      ? Object.values(entities).find(
          (entity) =>
            entity.context.kind === ContextKindKey.USER && entity.context.email === requestedKindKeyOrEmail.key,
        )
      : undefined;

  return userMatchByEmail
    ? { kind: getContextKind(userMatchByEmail.context), key: getContextKey(userMatchByEmail.context) }
    : requestedKindKeyOrEmail;
};

export const groupResolveContextsResponse = (
  entities: ContextItemsEntities,
  allRequestedTargets: OrderedSet<ContextTargetKindKey>,
): ResolveContextsGroups => {
  const allTargetKindKeys = allRequestedTargets.map((requestedKindKeyOrEmail) =>
    getMatchedKindKeyFromEntities(entities, requestedKindKeyOrEmail),
  );
  const found = allTargetKindKeys.filter((k) => entities.hasOwnProperty(makeKindKeyStringFromKindKey(k)));
  const notFound = allTargetKindKeys.filter((k) => !entities.hasOwnProperty(makeKindKeyStringFromKindKey(k)));
  return { all: allTargetKindKeys, found, notFound };
};

export const convertResolveContextsGroupsForUsers = (groups: ResolveContextsGroups) => ({
  all: groups.all.filter((t) => t.kind === ContextKindKey.USER).map((t) => t.key),
  found: groups.found.filter((t) => t.kind === ContextKindKey.USER).map((t) => t.key),
  notFound: groups.notFound.filter((t) => t.kind === ContextKindKey.USER).map((t) => t.key),
});

export const convertTargetsToKindKeyList = (
  targets: List<Target> | List<SegmentTarget>,
): List<ContextTargetKindKey> => {
  let kindKeys: List<ContextTargetKindKey> = List();
  targets.forEach((target) =>
    target.values.forEach((key) => (kindKeys = kindKeys.push({ kind: target.getContextKind(), key }))),
  );
  return kindKeys;
};

export const createMockContextItem = (context: LDContext): ContextItem => {
  const date = new Date('2018-07-01');
  const link = {
    href: '/api/v2/projects/default/environments/production',
    type: 'application/json',
  };
  const links = {
    parent: link,
    self: link,
    site: link,
  };
  return { lastSeen: date, applicationId: 'NodeJSClient', _links: links, context };
};

export type StrictGroupedOption = Omit<GroupedOption, 'options'> & { options: Array<{ label: string; value: string }> };
export const makeSelectContextKindOptions = ({
  contextKinds,
  evaluatedContextKindKeys,
  excludeKindAttribute,
}: {
  contextKinds: ContextKind[];
  evaluatedContextKindKeys: List<string>;
  excludeKindAttribute?: boolean;
}):
  | { groupedOptions: true; options: StrictGroupedOption[] }
  | { groupedOptions: false; options: Array<{ label: string; value: string }> } => {
  const generalOptions: Array<{ label: string; value: string }> = [];

  if (!excludeKindAttribute) {
    generalOptions.unshift({ label: 'Context kind', value: CONTEXT_KIND_ATTRIBUTE });
  }

  const formatContextKindLabel = (contextKind: ContextKind) => {
    if (!enableMobileTargeting) {
      return contextKind.name;
    } else {
      switch (contextKind.key) {
        case ContextKindKey.DEVICE:
          return 'Device';
        case ContextKindKey.APPLICATION:
          return 'Application';
        default:
          return contextKind.name;
      }
    }
  };

  const allContextKindOptions = contextKinds.map((k: ContextKind) => ({
    label: formatContextKindLabel(k),
    value: k.key,
  }));

  const legacyOptions = [...generalOptions, ...allContextKindOptions];

  const evaluatedContextKindOptions = allContextKindOptions.filter((o) => evaluatedContextKindKeys.contains(o.value));

  const unevaluatedContextKindOptions = allContextKindOptions.filter(
    (o) => !evaluatedContextKindKeys.contains(o.value),
  );

  const useGroups = hasMultipleGroups(generalOptions, evaluatedContextKindOptions, unevaluatedContextKindOptions);
  if (useGroups) {
    const generalOptionsGroup: StrictGroupedOption = { label: '', options: generalOptions };
    const evaluatedContextKindsGroup: StrictGroupedOption = {
      label: 'Recently seen context kinds',
      options: evaluatedContextKindOptions,
    };
    const unevaluatedLabel = evaluatedContextKindsGroup.options.length
      ? 'Other available context kinds'
      : 'Context kinds';
    const unevaluatedContextKindsGroup: StrictGroupedOption = {
      label: unevaluatedLabel,
      options: unevaluatedContextKindOptions,
    };
    return {
      groupedOptions: true,
      options: [generalOptionsGroup, evaluatedContextKindsGroup, unevaluatedContextKindsGroup].filter(
        (group) => group.options.length > 0,
      ),
    };
  }

  return { groupedOptions: false, options: legacyOptions };
};

const hasMultipleGroups = (...optionLists: OptionProps[]) => {
  let count = 0;
  for (const list of optionLists) {
    if (list.length > 0) {
      count++;
    }
    if (count > 1) {
      return true;
    }
  }
  return false;
};
